import {
  LegalDocLayout,
  LP, LH3, LUl, LLi, LNote, LTable,
  type LegalSection,
} from './LegalDocLayout';

const sections: LegalSection[] = [
  {
    id: 'overview',
    heading: 'Overview',
    body: (
      <>
        <LP>
          The GoGrabJob Business Trust Center is the single reference point for security,
          privacy, reliability, and compliance documentation. It is designed for procurement
          officers, information security teams, legal counsel, and compliance professionals
          evaluating GoGrabJob Business for enterprise deployment.
        </LP>
        <LP>
          GoGrabJob Business is developed and operated by <strong>Zykes Technologies LLP</strong>
          , registered in India. Our security programme is built on the principle of
          defence-in-depth: multiple overlapping controls are applied at every layer — from
          physical infrastructure through application logic to operational practice.
        </LP>
        <LP>
          This document reflects our current security posture as of <strong>April 2026</strong>.
          We update it quarterly or when material changes occur. Sensitive artefacts (penetration
          test reports, SOC 2 evidence packs, custom DPAs) are available under NDA to qualified
          Enterprise prospects. Contact{' '}
          <a
            href="mailto:security@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            security@gograbjob.com
          </a>{' '}
          to request access.
        </LP>
      </>
    ),
  },
  {
    id: 'certifications',
    heading: 'Certifications & Compliance Frameworks',
    body: (
      <>
        <LP>
          Our security programme is aligned to the following industry frameworks and regulatory
          requirements:
        </LP>
        <LTable
          headers={['Framework / Regulation', 'Status', 'Scope', 'Evidence Available']}
          rows={[
            ['SOC 2 Type II (AICPA)', 'Controls aligned; formal audit in progress (Q3 2026)', 'Security, Availability, Confidentiality, Privacy', 'Controls summary under NDA'],
            ['ISO/IEC 27001:2022', 'Controls aligned; certification target Q4 2026', 'ISMS for GoGrabJob Business platform', 'Gap assessment summary under NDA'],
            ['GDPR (EU) 2016/679', 'Compliant — DPA & SCCs available', 'All EEA customer and candidate data processing', 'DPA, SCCs, sub-processor list'],
            ['UK GDPR / Data Protection Act 2018', 'Compliant — IDTA addendum available', 'UK customer data', 'IDTA addendum to DPA'],
            ['India DPDPA 2023', 'Compliant (as regulations are issued)', 'All India resident data subjects', 'Privacy notice, consent management'],
            ['CCPA / CPRA', 'Compliant for California consumers', 'California resident personal data', 'Privacy Policy, opt-out mechanisms'],
            ['OWASP Top 10', 'Annually reviewed and mitigated', 'Application layer', 'Pentest summary under NDA'],
          ]}
        />
        <LNote>
          SOC 2 Type II and ISO 27001 certifications are in progress. In the interim, we provide
          a controls summary, evidence of penetration testing, and a security questionnaire
          response (aligned to the Consensus Assessments Initiative Questionnaire v4) under NDA
          to qualified Enterprise prospects.
        </LNote>
      </>
    ),
  },
  {
    id: 'infrastructure',
    heading: 'Infrastructure & Architecture',
    body: (
      <>
        <LH3>Cloud Provider</LH3>
        <LP>
          GoGrabJob Business is hosted entirely on <strong>Amazon Web Services (AWS)</strong>,
          leveraging AWS's extensive physical security certifications (ISO 27001, SOC 1/2/3,
          PCI-DSS, etc.). We use the following AWS regions:
        </LP>
        <LUl>
          <LLi>
            <strong>Primary:</strong> ap-south-1 (Mumbai, India) — all production workloads.
          </LLi>
          <LLi>
            <strong>EEA option:</strong> eu-west-1 (Ireland) — available to Enterprise customers
            requiring EEA data residency.
          </LLi>
          <LLi>
            <strong>Disaster Recovery:</strong> ap-southeast-1 (Singapore) — warm standby
            replication for RTO/RPO targets.
          </LLi>
        </LUl>

        <LH3>Architecture Principles</LH3>
        <LUl>
          <LLi>
            <strong>Multi-tenancy isolation:</strong> Customer data is logically isolated at the
            database and API layer using organisation-scoped tenant identifiers. Row-level security
            policies are enforced at the data layer.
          </LLi>
          <LLi>
            <strong>Microservices with private networking:</strong> Services communicate over a
            private VPC with no direct internet exposure; all external traffic is routed through
            load balancers with WAF rules.
          </LLi>
          <LLi>
            <strong>Immutable infrastructure:</strong> Production deployments use containerised
            images (Docker on ECS Fargate). No SSH access to production; all changes go through
            CI/CD pipelines with automated security scanning.
          </LLi>
          <LLi>
            <strong>Zero-trust network:</strong> Internal service-to-service communication
            authenticated via short-lived mTLS certificates; no implicit trust based on network
            location.
          </LLi>
          <LLi>
            <strong>Content delivery:</strong> Static assets served via Cloudflare CDN with DDoS
            protection, WAF, and Bot Fight Mode enabled.
          </LLi>
        </LUl>
      </>
    ),
  },
  {
    id: 'encryption',
    heading: 'Data Encryption',
    body: (
      <>
        <LH3>Encryption at Rest</LH3>
        <LP>All customer data stored in GoGrabJob Business is encrypted at rest using:</LP>
        <LUl>
          <LLi>
            <strong>AES-256-GCM</strong> for relational data (AWS RDS PostgreSQL with
            AWS KMS-managed keys).
          </LLi>
          <LLi>
            <strong>AES-256</strong> for object storage (AWS S3 with SSE-KMS; server-side
            encryption enabled on every bucket, enforced by S3 bucket policies that reject
            unencrypted PutObject calls).
          </LLi>
          <LLi>
            <strong>Column-level encryption</strong> for particularly sensitive fields (e.g.,
            third-party API keys, OAuth refresh tokens) using application-layer encryption before
            persisting to the database.
          </LLi>
        </LUl>
        <LNote>
          AWS KMS key rotation is set to automatic annual rotation. Customer-managed KMS keys
          (CMEK) are available for Enterprise customers, providing the ability to revoke key access
          and render data unreadable on demand.
        </LNote>

        <LH3>Encryption in Transit</LH3>
        <LUl>
          <LLi>
            All external traffic to GoGrabJob Business APIs and web interfaces is encrypted using
            <strong> TLS 1.3</strong> (TLS 1.2 minimum; 1.0 and 1.1 are disabled).
          </LLi>
          <LLi>
            HTTP Strict Transport Security (HSTS) headers with a max-age of 31536000 seconds and
            includeSubDomains are set on all endpoints.
          </LLi>
          <LLi>
            Internal service-to-service communication is encrypted via mutual TLS (mTLS) using
            certificates managed by AWS Certificate Manager Private CA.
          </LLi>
          <LLi>
            Database connections from application servers to AWS RDS use SSL-enforced connections
            with certificate verification.
          </LLi>
        </LUl>
      </>
    ),
  },
  {
    id: 'access-control',
    heading: 'Access Control & Authentication',
    body: (
      <>
        <LH3>Role-Based Access Control (RBAC)</LH3>
        <LP>
          GoGrabJob Business implements a five-tier RBAC model:
        </LP>
        <LTable
          headers={['Role', 'Typical User', 'Key Permissions']}
          rows={[
            ['Super Admin', 'Platform account owner', 'Full account control, billing, SSO config, user management'],
            ['Admin', 'HR Director / Operations Lead', 'All recruiting functions, team management, settings'],
            ['Recruiter', 'Recruiter / Sourcer', 'Create/manage jobs, view all candidates, run AI interviews'],
            ['Hiring Manager', 'Department head / Team lead', 'View pipeline for assigned roles, add scorecard notes'],
            ['Interviewer', 'Panel interviewer', 'View assigned candidates, submit scorecards only'],
          ]}
        />
        <LP>
          Custom roles with granular permission sets are available on the Enterprise plan.
          All permission changes are logged in the tamper-resistant audit log.
        </LP>

        <LH3>Authentication</LH3>
        <LUl>
          <LLi>
            <strong>Multi-factor authentication (MFA):</strong> TOTP (e.g., Google Authenticator,
            Authy) and WebAuthn / FIDO2 passkeys supported for all accounts. MFA enforcement can
            be mandated organisation-wide by admins.
          </LLi>
          <LLi>
            <strong>Single Sign-On (SSO):</strong> SAML 2.0 and OIDC supported on Growth and
            Enterprise plans, enabling integration with enterprise identity providers (Okta,
            Microsoft Entra ID, Google Workspace, Ping Identity, etc.).
          </LLi>
          <LLi>
            <strong>Session management:</strong> Idle session timeout (configurable, default 8
            hours); active session list visible to users; remote session revocation available.
          </LLi>
          <LLi>
            <strong>Password policy:</strong> Minimum 12 characters, complexity required; bcrypt
            hashing with high work factor; known breached password rejection via HaveIBeenPwned
            API (k-anonymity model).
          </LLi>
          <LLi>
            <strong>IP allowlisting:</strong> Organisations may restrict platform access to
            specified IP ranges or CIDRs (Enterprise plan).
          </LLi>
        </LUl>

        <LH3>Internal Access Controls</LH3>
        <LUl>
          <LLi>
            Zykes employees follow least-privilege access principles; production access requires
            approval and is time-limited.
          </LLi>
          <LLi>
            All production access by Zykes staff is logged; customer data is not accessed except
            with customer's written consent for support purposes.
          </LLi>
          <LLi>
            Access reviews are conducted quarterly; departing employees are de-provisioned within
            24 hours.
          </LLi>
        </LUl>
      </>
    ),
  },
  {
    id: 'vuln-management',
    heading: 'Vulnerability Management',
    body: (
      <>
        <LH3>Annual Penetration Testing</LH3>
        <LP>
          Zykes engages an independent CREST-accredited penetration testing firm to conduct a
          comprehensive black-box and grey-box penetration test of GoGrabJob Business annually.
          The scope covers:
        </LP>
        <LUl>
          <LLi>Web application layer (OWASP Top 10, business logic)</LLi>
          <LLi>API layer (authentication, authorisation, injection)</LLi>
          <LLi>Infrastructure (network exposure, AWS configuration review)</LLi>
        </LUl>
        <LP>
          Critical and high-severity findings are remediated within 14 and 30 days respectively.
          A summary findings report and remediation closure evidence are available under NDA to
          Enterprise prospects.
        </LP>

        <LH3>Continuous Scanning</LH3>
        <LUl>
          <LLi>
            <strong>Dependency scanning:</strong> All open-source dependencies scanned on every
            CI build using Dependabot and Snyk; critical CVEs trigger immediate patch pipelines.
          </LLi>
          <LLi>
            <strong>Container image scanning:</strong> Docker images scanned with Trivy before
            every production deployment; builds with critical unpatched vulnerabilities are
            blocked.
          </LLi>
          <LLi>
            <strong>Static Application Security Testing (SAST):</strong> CodeQL and Semgrep run
            on every pull request; security findings block merge.
          </LLi>
          <LLi>
            <strong>Infrastructure-as-Code scanning:</strong> Terraform plans scanned with Checkov
            before apply; misconfiguration alerts go to the security team.
          </LLi>
        </LUl>

        <LH3>Responsible Disclosure</LH3>
        <LP>
          We operate a responsible disclosure programme. If you discover a security vulnerability
          in GoGrabJob Business, please report it to{' '}
          <a
            href="mailto:security@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            security@gograbjob.com
          </a>{' '}
          with a description of the vulnerability and reproduction steps. We will acknowledge
          receipt within 24 hours, investigate promptly, and keep you informed of our progress.
          We ask that you do not publicly disclose the issue until we have had a reasonable
          opportunity to address it.
        </LP>
      </>
    ),
  },
  {
    id: 'incident-response',
    heading: 'Incident Response',
    body: (
      <>
        <LP>
          Zykes maintains a documented Incident Response Plan (IRP) that is reviewed and tested
          annually. The IRP covers:
        </LP>
        <LTable
          headers={['Phase', 'Activities', 'Owners']}
          rows={[
            ['Detection', 'Automated alerts from AWS GuardDuty, CloudWatch, Sentry, and WAF anomaly detection', 'Security team (24/7 paging)'],
            ['Containment', 'Isolate affected systems, rotate credentials, block malicious IPs, preserve forensic evidence', 'Engineering + Security'],
            ['Assessment', 'Determine scope, data involved, root cause, and regulatory notification requirements', 'Security + Legal + DPO'],
            ['Notification', 'Customer notification within 72 hours (GDPR Art. 33) if applicable; supervisory authority notification as required', 'Legal + Customer Success'],
            ['Remediation', 'Patch vulnerability, harden defences, validate fix', 'Engineering'],
            ['Post-incident review', 'Root cause analysis, lessons learned, control improvements', 'All teams'],
          ]}
        />
        <LP>
          Incident severity is classified on a P1–P4 scale. P1 (critical) incidents trigger
          immediate 24/7 response. All production incidents are logged, and post-mortems for
          customer-impacting events are published to affected customers within 5 business days.
        </LP>
        <LP>
          To report a security incident or suspected breach, email{' '}
          <a
            href="mailto:security@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            security@gograbjob.com
          </a>{' '}
          with subject "SECURITY INCIDENT" for immediate escalation.
        </LP>
      </>
    ),
  },
  {
    id: 'bcdr',
    heading: 'Business Continuity & Disaster Recovery',
    body: (
      <>
        <LH3>Recovery Objectives</LH3>
        <LTable
          headers={['Tier', 'Service', 'RTO', 'RPO']}
          rows={[
            ['Tier 1 (Critical)', 'Core ATS pipeline, candidate APIs, authentication', '< 1 hour', '< 15 minutes'],
            ['Tier 2 (Important)', 'Analytics, reporting, AI interview scoring', '< 4 hours', '< 1 hour'],
            ['Tier 3 (Standard)', 'Email notifications, audit log export, webhooks', '< 8 hours', '< 4 hours'],
          ]}
        />

        <LH3>Data Backup</LH3>
        <LUl>
          <LLi>
            <strong>Automated daily snapshots:</strong> AWS RDS automated backups with
            point-in-time recovery enabled; retained for 35 days.
          </LLi>
          <LLi>
            <strong>Cross-region replication:</strong> Daily snapshots are replicated to the DR
            region (ap-southeast-1) for geographic redundancy.
          </LLi>
          <LLi>
            <strong>Object storage versioning:</strong> S3 versioning enabled on all buckets
            containing customer data; 90-day version retention.
          </LLi>
          <LLi>
            <strong>Backup integrity checks:</strong> Monthly automated restoration tests confirm
            that backups are viable and meet RPO requirements.
          </LLi>
        </LUl>

        <LH3>Availability</LH3>
        <LP>
          GoGrabJob Business targets <strong>99.9% uptime SLA</strong> for Growth tier customers
          and <strong>99.99% uptime SLA</strong> for Enterprise tier. Live uptime data and
          incident history are published at{' '}
          <a
            href="https://status.gograbjob.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            status.gograbjob.com
          </a>
          .
        </LP>
      </>
    ),
  },
  {
    id: 'sub-processors',
    heading: 'Sub-Processors & Third Parties',
    body: (
      <>
        <LP>
          All third-party sub-processors engaged by Zykes to process customer or candidate data
          are bound by written data processing agreements and assessed for security and privacy
          compliance before onboarding.
        </LP>
        <LTable
          headers={['Sub-processor', 'Category', 'Data Processed', 'Certifications']}
          rows={[
            ['Amazon Web Services', 'Cloud infrastructure', 'All data at rest and in processing', 'ISO 27001, SOC 1/2/3, PCI-DSS'],
            ['Stripe', 'Payment processing', 'Billing contact, payment metadata', 'PCI-DSS Level 1, SOC 1/2'],
            ['Twilio / SendGrid', 'Transactional email', 'Recipient email, notification content', 'ISO 27001, SOC 2'],
            ['Cloudflare', 'CDN / WAF / DDoS protection', 'IP addresses, request headers (no body data)', 'ISO 27001, SOC 2'],
            ['PostHog (self-hosted)', 'Product analytics', 'Anonymised usage events', 'Self-hosted on Zykes infra (no third-party transfer)'],
            ['Intercom', 'Support chat', 'Name, email, support conversation', 'ISO 27001, SOC 2, Privacy Shield successor (SCCs)'],
            ['Sentry (self-hosted)', 'Error monitoring', 'Stack traces, anonymised session IDs', 'Self-hosted on Zykes infra'],
          ]}
        />
        <LP>
          The complete and authoritative sub-processor list is maintained as a versioned document.
          Customers are notified 30 days in advance of material sub-processor additions or
          changes. Request the current sub-processor list from{' '}
          <a
            href="mailto:security@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            security@gograbjob.com
          </a>
          .
        </LP>
      </>
    ),
  },
  {
    id: 'requesting-documents',
    heading: 'Requesting Security Documentation',
    body: (
      <>
        <LP>
          The following documents are available to customers and qualified Enterprise prospects:
        </LP>
        <LTable
          headers={['Document', 'Availability', 'How to Request']}
          rows={[
            ['Data Processing Agreement (DPA)', 'All paid customers', 'Account Settings → Legal Documents'],
            ['Standard Contractual Clauses (SCCs)', 'EEA/UK customers', 'Included in DPA'],
            ['IDTA addendum (UK)', 'UK customers', 'Included in DPA on request'],
            ['Sub-processor list (current + history)', 'All customers', 'security@gograbjob.com'],
            ['Security Overview document', 'All customers', 'Account Settings → Trust Center'],
            ['Penetration test summary', 'Enterprise + under NDA', 'security@gograbjob.com'],
            ['SOC 2 controls summary', 'Enterprise + under NDA', 'security@gograbjob.com'],
            ['Security questionnaire (CAIQ v4)', 'Qualified Enterprise prospects', 'security@gograbjob.com'],
            ['DPIA / Privacy Impact Assessment', 'Enterprise + under NDA', 'dpo@gograbjob.com'],
            ['Business Associate Agreement (BAA)', 'Healthcare customers', 'legal@gograbjob.com'],
          ]}
        />
        <LP>
          To request any document or engage with our security team for a vendor assessment, please
          email{' '}
          <a
            href="mailto:security@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            security@gograbjob.com
          </a>{' '}
          with your company name, role, and the specific documents required. We respond to
          security questionnaires within 5 business days.
        </LP>
      </>
    ),
  },
  {
    id: 'contact-security',
    heading: 'Contact Our Security Team',
    body: (
      <>
        <LP>
          For all security, compliance, and trust-related enquiries:
        </LP>
        <LUl>
          <LLi>
            <strong>General security enquiries:</strong>{' '}
            <a
              href="mailto:security@gograbjob.com"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              security@gograbjob.com
            </a>
          </LLi>
          <LLi>
            <strong>Vulnerability reports:</strong>{' '}
            <a
              href="mailto:security@gograbjob.com"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              security@gograbjob.com
            </a>{' '}
            — subject: "VULNERABILITY REPORT"
          </LLi>
          <LLi>
            <strong>Data protection / GDPR / DPO:</strong>{' '}
            <a
              href="mailto:dpo@gograbjob.com"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              dpo@gograbjob.com
            </a>
          </LLi>
          <LLi>
            <strong>Legal / contract review:</strong>{' '}
            <a
              href="mailto:legal@gograbjob.com"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              legal@gograbjob.com
            </a>
          </LLi>
          <LLi>
            <strong>Enterprise procurement:</strong>{' '}
            <a
              href="mailto:enterprise@gograbjob.com"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              enterprise@gograbjob.com
            </a>
          </LLi>
        </LUl>
        <LP>
          Live system availability:{' '}
          <a
            href="https://status.gograbjob.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            status.gograbjob.com
          </a>
        </LP>
        <LP>
          All enquiries receive an initial response within one business day. Security incidents
          are triaged within 1 hour.
        </LP>
      </>
    ),
  },
];

export function TrustCenterPage() {
  return (
    <LegalDocLayout
      category="Legal · Security"
      title="Trust Center"
      subtitle="Security architecture, certifications, compliance artefacts, and sub-processor documentation for GoGrabJob Business — everything your InfoSec and procurement teams need."
      effectiveDate="1 April 2026"
      lastUpdated="14 April 2026"
      version="2.1"
      sections={sections}
      contactEmail="security@gograbjob.com"
    />
  );
}
